import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
	getNewEnrollmentsOptions,
	getNewEnrollmentsQueryKey,
} from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	ArrowLeft,
	Phone,
	Mail,
	MapPin,
	CreditCard,
	User,
	Calendar,
	ExternalLink,
	Check,
} from "lucide-react";

export const Route = createFileRoute("/admin/new-enrollments/$id")({
	component: NewEnrollmentDetailsPage,
});

const subjectLabels: Record<string, string> = {
	MATHS: "Mathématiques",
	FRANCAIS: "Français",
	SCIENCES_PHYSIQUE: "Sciences Physiques",
	ANGLAIS: "Anglais",
	SES: "SES",
};

const timeSlotLabels: Record<string, string> = {
	SATURDAY_MORNING_1: "Samedi 8h30 - 10h30",
	SATURDAY_MORNING_2: "Samedi 10h30 - 12h30",
	SATURDAY_AFTERNOON_1: "Samedi 13h30 - 15h30",
	SATURDAY_AFTERNOON_2: "Samedi 15h30 - 17h30",
	WEDNESDAY_MORNING_1: "Mercredi 11h30 - 13h30",
};

const paymentMethodLabels: Record<string, string> = {
	CARD: "Carte bancaire",
	SEPA: "Prélèvement SEPA",
	BANK_TRANSFER: "Virement bancaire",
};

function NewEnrollmentDetailsPage() {
	const { id } = Route.useParams();

	const { data: parents = [], isLoading } = useQuery(
		getNewEnrollmentsOptions(),
	);

	const parent = parents.find((p) => p.id === id);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
			</div>
		);
	}

	if (!parent) {
		return (
			<div className="p-6">
				<div className="text-center">
					<h2 className="text-2xl font-bold text-gray-900 mb-4">
						Parent non trouvé
					</h2>
					<Link to="/admin/new-enrollments">
						<Button variant="outline">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Retour à la liste
						</Button>
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Link to="/admin/new-enrollments">
						<Button variant="outline" size="sm">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Retour
						</Button>
					</Link>
					<div>
						<h1 className="text-3xl font-bold">
							{parent.firstName} {parent.lastName}
						</h1>
						<p className="text-gray-600">Nouvelle inscription</p>
					</div>
				</div>
				<Link to="/admin/parents/$id" params={{ id: parent.id || "" }}>
					<Button>
						<ExternalLink className="mr-2 h-4 w-4" />
						Voir le profil complet
					</Button>
				</Link>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<Card className="lg:col-span-2">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<User className="h-5 w-5" />
							Informations parent
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<p className="text-sm font-medium text-gray-500">Prénom</p>
								<p className="mt-1">{parent.firstName}</p>
							</div>
							<div>
								<p className="text-sm font-medium text-gray-500">Nom</p>
								<p className="mt-1">{parent.lastName}</p>
							</div>
						</div>

						<div className="flex items-center gap-2 text-gray-700">
							<Mail className="h-4 w-4" />
							<span>{parent.email}</span>
						</div>

						<div className="flex items-center gap-2 text-gray-700">
							<Phone className="h-4 w-4" />
							<span>{parent.phone}</span>
						</div>

						<div className="flex items-start gap-2 text-gray-700">
							<MapPin className="h-4 w-4 mt-1" />
							<div>
								<p>{parent.address}</p>
								{parent.addressLatitude && parent.addressLongitude && (
									<p className="text-sm text-gray-500 mt-1">
										Coordonnées GPS: {parent.addressLatitude.toFixed(6)},{" "}
										{parent.addressLongitude.toFixed(6)}
									</p>
								)}
							</div>
						</div>

						<div className="flex items-center gap-2 text-gray-700">
							<CreditCard className="h-4 w-4" />
							<Badge variant="secondary">
								{parent.paymentMethod
									? paymentMethodLabels[parent.paymentMethod] ||
										parent.paymentMethod
									: "Non renseigné"}
							</Badge>
						</div>

						{parent.comments && (
							<div>
								<p className="text-sm font-medium text-gray-500 mb-1">
									Commentaires
								</p>
								<p className="text-gray-700 whitespace-pre-wrap">
									{parent.comments}
								</p>
							</div>
						)}

						<div className="flex items-center gap-2 text-gray-700">
							<Calendar className="h-4 w-4" />
							<span className="text-sm">
								Inscrit le{" "}
								{parent.createdAt
									? new Date(parent.createdAt).toLocaleDateString("fr-FR", {
											day: "2-digit",
											month: "long",
											year: "numeric",
											hour: "2-digit",
											minute: "2-digit",
										})
									: "-"}
							</span>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Résumé</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<div>
							<p className="text-sm text-gray-500">Nombre d'enfants</p>
							<p className="text-2xl font-bold">
								{parent.students?.length || 0}
							</p>
						</div>
						<div>
							<p className="text-sm text-gray-500">Statut</p>
							<Badge variant="outline" className="mt-1">
								En attente d'affectation
							</Badge>
						</div>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Étudiants ({parent.students?.length || 0})</CardTitle>
					<CardDescription>
						Liste des enfants avec leurs besoins pédagogiques
					</CardDescription>
				</CardHeader>
				<CardContent>
					{!parent.students || parent.students.length === 0 ? (
						<div className="text-center py-8 text-gray-500">
							Aucun étudiant enregistré
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Nom</TableHead>
									<TableHead>École</TableHead>
									<TableHead>Niveau</TableHead>
									<TableHead>Classe</TableHead>
									<TableHead>Matières</TableHead>
									<TableHead>Créneaux</TableHead>
									<TableHead>Besoins</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{parent.students.map((student) => (
									<TableRow key={student.id}>
										<TableCell className="font-medium">
											{student.firstName} {student.lastName}
										</TableCell>
										<TableCell>
											{student.schoolOther || student.school || "-"}
										</TableCell>
										<TableCell>{student.level || "-"}</TableCell>
										<TableCell>{student.className || "-"}</TableCell>
										<TableCell>
											<div className="flex flex-wrap gap-1">
												{student.subjects && student.subjects.length > 0 ? (
													student.subjects.map((subject) => (
														<Badge
															key={subject}
															variant="secondary"
															className="text-xs"
														>
															{subjectLabels[subject] || subject}
														</Badge>
													))
												) : (
													<span className="text-gray-400">-</span>
												)}
											</div>
										</TableCell>
										<TableCell>
											<div className="flex flex-wrap gap-1">
												{student.timeSlots && student.timeSlots.length > 0 ? (
													student.timeSlots.map((slot) => (
														<Badge
															key={slot}
															variant="outline"
															className="text-xs"
														>
															{timeSlotLabels[slot] || slot}
														</Badge>
													))
												) : (
													<span className="text-gray-400">-</span>
												)}
											</div>
										</TableCell>
										<TableCell>
											<div className="flex gap-2">
												{student.hasPap && (
													<Badge variant="default" className="text-xs">
														<Check className="h-3 w-3 mr-1" />
														PAP
													</Badge>
												)}
												{student.hasPps && (
													<Badge variant="default" className="text-xs">
														<Check className="h-3 w-3 mr-1" />
														PPS
													</Badge>
												)}
												{!student.hasPap && !student.hasPps && (
													<span className="text-gray-400">-</span>
												)}
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
